import type { ReactNode } from 'react';
import { GlassCard } from '@/design-system';
import type { PipelineActor, PipelineCellRead, PipelineStageId } from './PipelineStrip.transform';
import { PIPELINE_STAGES } from './PipelineStrip.transform';

/**
 * Actor glyphs from the locked comp (`advisory-flow-and-case.html`): one mark
 * per kind of actor, so "who acts" reads by shape before it reads by word.
 */
function ActorGlyph({ actor }: { actor: PipelineActor }) {
  const common = { viewBox: '0 0 10 10', className: 'size-2.5 shrink-0', 'aria-hidden': true } as const;
  switch (actor) {
    case 'System':
      return (
        <svg {...common}>
          <circle cx="5" cy="5" r="3.6" fill="none" stroke="currentColor" strokeWidth="1.6" />
          <circle cx="5" cy="5" r="1.2" fill="currentColor" />
        </svg>
      );
    case 'Engine':
      return (
        <svg {...common}>
          <rect x="1" y="1" width="8" height="8" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.6" />
          <rect x="3.5" y="3.5" width="3" height="3" fill="currentColor" />
        </svg>
      );
    case 'Decision model':
      return (
        <svg {...common}>
          <path d="M5 1 L9 9 L1 9 Z" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
        </svg>
      );
    case 'Agent':
      return (
        <svg {...common}>
          <path d="M5 0.5 L6.1 3.9 L9.5 5 L6.1 6.1 L5 9.5 L3.9 6.1 L0.5 5 L3.9 3.9 Z" fill="currentColor" />
        </svg>
      );
    case 'Human':
      return (
        <svg {...common}>
          <circle cx="5" cy="3.3" r="2.1" fill="currentColor" />
          <path d="M1.2 9.5 C1.6 6.8 3 5.9 5 5.9 C7 5.9 8.4 6.8 8.8 9.5 Z" fill="currentColor" />
        </svg>
      );
  }
}

/** One actor chip: the agent carries the accent, a human carries full ink.
 *  `wrap` lets the seat label run inside the pill onto a second line when a
 *  dense table column is narrower than the label; without it the label keeps
 *  its one line and truncates, which is what every other surface reads. */
export function ActorChip({ actor, children, wrap = false }: { actor: PipelineActor; children?: ReactNode; wrap?: boolean }) {
  const tone = actor === 'Agent'
    ? 'border-[color-mix(in_srgb,var(--color-accent-500)_40%,transparent)] text-[var(--color-accent-600)] dark:text-[var(--color-accent-300)]'
    : actor === 'Human'
      ? 'border-[var(--hairline-strong)] text-ink-primary'
      : 'border-[var(--hairline-strong)] text-ink-secondary';
  return (
    <span
      data-actor-chip={actor}
      className={`inline-flex max-w-full items-center gap-0.5 rounded-full border bg-[var(--surface-2)] py-px pl-1 pr-0.5 text-[10px] font-semibold uppercase leading-4 tracking-[0.05em] ${wrap ? 'text-left' : 'whitespace-nowrap'} ${tone}`}
    >
      <ActorGlyph actor={actor} />
      <span className={wrap ? 'min-w-0' : 'truncate'}>{children ?? actor}</span>
    </span>
  );
}

/** The lead term of a comparison ("open · 1 new since shift start") carries the weight; the rest is context. */
function Comparison({ text }: { text: string }) {
  const split = text.indexOf(' · ');
  if (split < 0) return <span className="font-semibold text-ink-secondary">{text}</span>;
  return (
    <>
      <span className="font-semibold text-ink-secondary">{text.slice(0, split)}</span>
      {text.slice(split)}
    </>
  );
}

/** A stage the strip draws. Advisory's eight are the default; another route
 *  whose objects move through a fixed, ordered sequence of stages (the Cost
 *  route's improvement pipeline, polish.P7) passes its own, so the product has
 *  one strip object (DESIGN.md § Strip and stepper). */
export type PipelineStripStage<Id extends string> = {
  id: Id;
  label: string;
  actors: readonly PipelineActor[];
};

export function PipelineStrip<Id extends string = PipelineStageId>({
  activeStage,
  cells,
  arrivingStage,
  onSelect,
  mode = 'tabs',
  stages,
  navLabel = 'Advisory pipeline',
  idPrefix = 'pipeline',
  panelId = 'pipeline-stage-panel',
}: {
  activeStage: Id | null;
  cells: Readonly<Record<Id, PipelineCellRead>>;
  arrivingStage?: Id | null;
  marker?: { label: string; stage: Id } | null;
  onSelect: (stage: Id) => void;
  /** The default strip stays a tab bar; case pages use the same stages as progress. */
  mode?: 'tabs' | 'stepper';
  /** Defaults to Advisory's eight stages. */
  stages?: readonly PipelineStripStage<Id>[];
  navLabel?: string;
  idPrefix?: string;
  panelId?: string;
}) {
  const STAGES = (stages ?? PIPELINE_STAGES) as readonly PipelineStripStage<Id>[];
  const tabGrid = STAGES.length === 8 ? 'grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-8' : 'grid grid-cols-2 sm:grid-cols-4';
  const activeIndex = STAGES.findIndex((row) => row.id === activeStage);
  const statusOf = (index: number, selected: boolean) =>
    index < activeIndex ? 'done' : selected ? 'current' : 'next';

  if (mode === 'stepper') {
    return (
      <nav aria-label="Case pipeline progress" className="pb-1">
        <div
          role="list"
          aria-label="Case pipeline stages"
          className="grid grid-cols-2 gap-1.5 sm:grid-cols-4 lg:grid-cols-8"
        >
          {STAGES.map((stage, index) => {
            const read = cells[stage.id];
            const selected = activeStage === stage.id;
            const status = statusOf(index, selected);
            return (
              <div
                key={stage.id}
                role="listitem"
                data-stage-id={stage.id}
                data-stage-status={status}
                aria-current={selected ? 'step' : undefined}
                className={`flex min-h-[4.75rem] min-w-0 flex-col gap-0.5 rounded-[10px] border px-2.5 py-2 ${
                  selected
                    ? 'border-[var(--color-accent-500)] border-t-[3px] bg-[color-mix(in_srgb,var(--color-accent-500)_8%,var(--surface-2))]'
                    : status === 'done'
                      ? 'border-[var(--hairline)] border-t-[3px] border-t-[var(--ink-tertiary)] bg-[var(--surface-2)]'
                      : 'border-dashed border-[var(--hairline-strong)] bg-transparent'
                }`}
              >
                <span className={`text-xs font-semibold uppercase tracking-[0.07em] ${selected ? 'text-ink-secondary' : 'text-muted'}`}>
                  {stage.label}
                  <span className="sr-only"> {status}</span>
                </span>
                <span data-stepper-count={stage.id} className="break-words text-xs font-semibold leading-snug text-ink-primary" title={read.count}>
                  {read.count}
                </span>
                {read.countContext ? (
                  <span className="text-xs leading-snug text-muted">{read.countContext}</span>
                ) : null}
                <span data-stepper-comparison={stage.id} className="break-words text-xs leading-snug text-muted">
                  {read.comparison}
                </span>
                {read.money ? (
                  <span className="mt-auto break-words pt-1 text-xs font-semibold leading-snug tabular-nums text-ink-primary">
                    {read.money}
                    {read.moneyComparison ? <span data-stepper-money-comparison={stage.id} className="block font-normal text-muted">{read.moneyComparison}</span> : null}
                  </span>
                ) : null}
              </div>
            );
          })}
        </div>
      </nav>
    );
  }

  return (
    <div className="relative">
      <GlassCard
        as="nav"
        tone="matte"
        aria-label={navLabel}
        className="relative overflow-hidden rounded-2xl border-[var(--hairline-strong)] p-0"
      >
        <div
          role="tablist"
          aria-label="Pipeline stages"
          className={tabGrid}
        >
          {/* K18 r1: the US$ glyph is one character wider than the pre-D26 one;
              the tabs' right pad drops to pr-1 so `US$4,664/day at stake` and
              `US$739/day captured` keep their pre-D26 single line without moving
              the left inset. */}
          {STAGES.map((stage) => {
            const read = cells[stage.id];
            const selected = activeStage === stage.id;
            const arriving = arrivingStage === stage.id;
            return (
              <button
                key={stage.id}
                id={`${idPrefix}-tab-${stage.id.replace(/\s+/g, '-')}`}
                type="button"
                role="tab"
                aria-label={stage.label}
                aria-selected={selected}
                aria-controls={panelId}
                data-stage-id={stage.id}
                onClick={() => onSelect(stage.id)}
                className={`group relative flex min-h-[7.5rem] min-w-0 flex-col gap-1 border-b border-r border-[var(--hairline)] py-3 pl-3.5 pr-1 text-left transition-colors last:border-r-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--color-accent-500)] xl:border-b-0 after:pointer-events-none after:absolute after:right-[-6px] after:top-1/2 after:z-10 after:hidden after:h-[10px] after:w-[10px] after:-translate-y-1/2 after:rotate-45 after:border-r after:border-t after:border-[var(--hairline-strong)] after:content-[''] last:after:hidden xl:after:block xl:last:after:hidden ${
                  selected
                    ? 'bg-[color-mix(in_srgb,var(--color-accent-500)_8%,var(--surface-2))] shadow-[inset_0_-3px_0_var(--color-accent-500)] after:bg-[color-mix(in_srgb,var(--color-accent-500)_8%,var(--surface-2))]'
                    : 'bg-[var(--surface-2)] after:bg-[var(--surface-2)] hover:bg-[var(--surface-1)] hover:after:bg-[var(--surface-1)]'
                }`}
              >
                <span className="text-xs font-semibold uppercase tracking-[0.07em] text-muted">
                  {stage.label}
                </span>
                <span
                  className={`flex items-baseline gap-1 text-2xl font-semibold leading-none tracking-[-0.01em] tabular-nums transition-colors duration-300 ${
                    arriving ? 'text-[var(--color-accent-600)] dark:text-[var(--color-accent-300)]' : 'text-ink-primary'
                  }`}
                  data-stage-count={stage.id}
                >
                  {read.count}
                  {read.countContext ? (
                    <span className="text-xs font-medium tracking-normal text-muted">{read.countContext}</span>
                  ) : null}
                </span>
                <span className="text-xs leading-snug text-muted">
                  {read.comparisonLead ? (
                    <>
                      <span className="font-semibold text-ink-secondary">{read.comparisonLead}</span> {read.comparison}
                    </>
                  ) : (
                    <Comparison text={read.comparison} />
                  )}
                </span>
                {read.money ? (
                  <span className="text-xs leading-snug tabular-nums">
                    <span className="block font-semibold text-ink-primary">{read.money}</span>
                    {read.moneyComparison ? <span className="block text-muted">{read.moneyComparison}</span> : null}
                  </span>
                ) : null}
                <span data-stage-actors={stage.id} className="mt-auto flex min-w-0 flex-wrap gap-1 pt-1">
                  {/* #786: a cell is about 115 px at 1280, narrower than the
                      `Decision model` seat label, so the chip wraps onto a
                      second line rather than ellipsising. At 1470 the same
                      labels keep one line; other surfaces' chips are unchanged. */}
                  {stage.actors.map((actor) => (
                    <ActorChip key={actor} actor={actor} wrap>{read.actorCounts?.[actor]}</ActorChip>
                  ))}
                </span>
              </button>
            );
          })}
        </div>
        <div aria-hidden="true" data-marker-lane className="pointer-events-none absolute inset-x-0 bottom-0 h-0" />
      </GlassCard>
    </div>
  );
}
